/**
 * @fileoverview The good and bad options a spy ticks when checking on a BA,
 * edited per city.
 *
 * Saves on its own, through its own mutation, so it never holds up the city
 * form. A city with no list keeps the app's built-in one; "Start from the
 * app's list" copies that list in so ops edit rather than retype.
 *
 * The English label is what lands in the spy report, so it cannot contain a
 * comma. "Other" with free text is always offered by the app.
 */
import React, { useContext, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'

import { readGraphQLData } from 'Components/PowerBanks/hooks'
import SubmitButton from 'Components/Form/SubmitButton'
import { SnackBarContext } from 'Components/SnackBar'
import {
  fetchCitySpyReportOptions,
  updateCitySpyReportOptions,
} from 'Module/graphQL'

/** The app's built-in list, the starting point when a city has none. */
export const APP_REPORT_OPTIONS = [
  { kind: 'good', en: 'Proactive BA', he: 'שגריר יוזם' },
  { kind: 'good', en: 'BA engaging with crowd', he: 'שגריר מעורב עם הקהל' },
  {
    kind: 'good',
    en: 'Distribution over 5F/min',
    he: 'חלוקה מעל 5 פליירים/דקה',
  },
  {
    kind: 'good',
    en: 'Distribution between 3-5F/min',
    he: 'חלוקה בין 3-5 פליירים/דקה',
  },
  { kind: 'good', en: 'High traffic location', he: 'מיקום עם תנועה רבה' },
  {
    kind: 'good',
    en: 'BA finished all flyers',
    he: 'שגריר סיים את כל הפליירים',
  },
  { kind: 'good', en: 'Client pitch known', he: 'הצעת הלקוח ידועה' },
  { kind: 'good', en: 'Obedient BA', he: 'שגריר ממושמע' },
  { kind: 'good', en: 'BA on time', he: 'שגריר הגיע בזמן' },
  { kind: 'bad', en: 'Passive BA', he: 'שגריר פסיבי' },
  {
    kind: 'bad',
    en: 'Distribution below 3F/min',
    he: 'חלוקה מתחת ל-3 פליירים/דקה',
  },
  { kind: 'bad', en: 'Bad location', he: 'מיקום גרוע' },
  { kind: 'bad', en: 'Distribution by 2 and more', he: 'חלוקה על ידי 2 ויותר' },
  {
    kind: 'bad',
    en: 'BA disappeared and not reachable',
    he: 'שגריר נעלם ולא זמין',
  },
  { kind: 'bad', en: 'BA refuse listening', he: 'שגריר מסרב להקשיב' },
  { kind: 'bad', en: 'BA throwing away flyers', he: 'שגריר זורק פליירים' },
  { kind: 'bad', en: 'No uniform', he: 'ללא מדים' },
  { kind: 'bad', en: 'Training needed for BA', he: 'נדרשת הדרכה לשגריר' },
  { kind: 'bad', en: 'BA late', he: 'שגריר איחר' },
  { kind: 'bad', en: 'BA does not communicate', he: 'שגריר לא מתקשר' },
]

const Wrapper = styled.section`
  margin: 24px 0;
  padding: 20px;
  border: 1px solid #eceef0;
  border-radius: 4px;
  background: #ffffff;
  max-width: 820px;

  h3 {
    margin: 0 0 4px;
    font-size: 18px;
  }
  .intro,
  .hint {
    color: #5a6b82;
    font-size: 13px;
  }
  .intro {
    margin: 0 0 14px;
  }
  .option {
    display: grid;
    grid-template-columns: 90px minmax(0, 1fr) minmax(0, 1fr) 32px;
    gap: 8px;
    align-items: center;
    margin-bottom: 6px;
  }
  .option select,
  .option input {
    width: 100%;
    padding: 5px 8px;
    border: 1px solid #d9dee6;
    border-radius: 4px;
    min-width: 0;
  }
  .option button.remove {
    border: none;
    background: none;
    color: #c0392b;
    font-size: 18px;
    cursor: pointer;
  }
  .head {
    font-size: 12px;
    font-weight: 600;
    color: #5a6b82;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  .actions {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    align-items: center;
    margin-top: 14px;
  }
  .link {
    border: none;
    background: none;
    color: #1878cf;
    cursor: pointer;
    padding: 0;
    font-size: 14px;
  }
`

const blankOption = () => ({ kind: 'bad', en: '', he: '' })

const SpyReportOptions = ({ cityId }) => {
  const runSnackBar = useContext(SnackBarContext)
  const [state, setState] = useState('loading')
  const [options, setOptions] = useState([])
  const [usesAppList, setUsesAppList] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!cityId) return
    let cancelled = false
    const load = async () => {
      try {
        const data = await readGraphQLData(
          await fetchCitySpyReportOptions({ cityId }),
        )
        if (cancelled) return
        const spySettings = data.city && data.city.spySettings
        if (!spySettings) return setState('noSpySettings')
        const saved = spySettings.reportOptions
        setUsesAppList(!Array.isArray(saved) || !saved.length)
        setOptions(Array.isArray(saved) && saved.length ? saved : [])
        setState('ready')
      } catch (error) {
        if (!cancelled) setState('failed')
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [cityId])

  const update = (index, field, value) =>
    setOptions(current =>
      current.map((option, i) =>
        i === index ? { ...option, [field]: value } : option,
      ),
    )

  const save = async nextOptions => {
    setSaving(true)
    try {
      const data = await readGraphQLData(
        await updateCitySpyReportOptions({ cityId, options: nextOptions }),
      )
      const saved = data.updateCitySpyReportOptions.reportOptions
      setUsesAppList(!Array.isArray(saved) || !saved.length)
      setOptions(Array.isArray(saved) ? saved : [])
      runSnackBar({
        type: 'success',
        msg:
          Array.isArray(saved) && saved.length
            ? `Saved. Spies in this city now see ${
                saved.length
              } options, plus Other.`
            : "Saved. Spies in this city see the app's own list.",
      })
    } catch (error) {
      runSnackBar({
        type: 'error',
        msg: (error && error.message) || 'Could not save the options.',
      })
    } finally {
      setSaving(false)
    }
  }

  if (state === 'loading') return null
  if (state === 'noSpySettings') return null

  return (
    <Wrapper>
      <h3>Spy report options</h3>
      <p className="intro">
        What a spy can tick when checking on a BA. Other, with a text box, is
        always there too.
      </p>

      {state === 'failed' ? (
        <p className="hint">Could not load the options. Reload the page.</p>
      ) : (
        <>
          {usesAppList && !options.length ? (
            <p className="hint">
              This city uses the app&apos;s own list of{' '}
              {APP_REPORT_OPTIONS.length} options.{' '}
              <button
                type="button"
                className="link"
                onClick={() => setOptions(APP_REPORT_OPTIONS)}
              >
                Start from the app&apos;s list
              </button>
            </p>
          ) : (
            <>
              <div className="option head">
                <span>Kind</span>
                <span>English</span>
                <span>Hebrew</span>
                <span />
              </div>
              {options.map((option, index) => (
                <div className="option" key={`option-${index}`}>
                  <select
                    id={`spy-option-kind-${index}`}
                    aria-label="Good or bad"
                    value={option.kind}
                    onChange={event =>
                      update(index, 'kind', event.target.value)
                    }
                  >
                    <option value="good">Good</option>
                    <option value="bad">Bad</option>
                  </select>
                  <input
                    id={`spy-option-en-${index}`}
                    aria-label="English"
                    value={option.en}
                    maxLength={60}
                    onChange={event => update(index, 'en', event.target.value)}
                  />
                  <input
                    id={`spy-option-he-${index}`}
                    aria-label="Hebrew"
                    dir="rtl"
                    value={option.he || ''}
                    maxLength={60}
                    onChange={event => update(index, 'he', event.target.value)}
                  />
                  <button
                    type="button"
                    className="remove"
                    aria-label={`Remove ${option.en || 'option'}`}
                    onClick={() =>
                      setOptions(current =>
                        current.filter((_, i) => i !== index),
                      )
                    }
                  >
                    ×
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="link"
                onClick={() =>
                  setOptions(current => [...current, blankOption()])
                }
              >
                Add an option
              </button>
            </>
          )}

          <div className="actions">
            <SubmitButton
              value="Save spy options"
              size="small"
              disabled={saving || (usesAppList && !options.length)}
              onClick={() => save(options)}
            />
            {!usesAppList ? (
              <button
                type="button"
                className="link"
                disabled={saving}
                onClick={() => save(null)}
              >
                Go back to the app&apos;s list
              </button>
            ) : null}
            <span className="hint">No commas in an option.</span>
          </div>
        </>
      )}
    </Wrapper>
  )
}

SpyReportOptions.propTypes = {
  cityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}

SpyReportOptions.defaultProps = {
  cityId: null,
}

export default SpyReportOptions
